/**
 * useSessionFiles — persists uploaded file metadata per session in localStorage.
 *
 * When the user uploads a file and sends it, the S3 key lives on the server
 * for 30 days but the frontend state is cleared. This hook keeps a registry of
 * every successfully-uploaded file for a given session so the user can
 * re-attach files from previous messages without re-uploading.
 *
 * Storage key: `wayne.v1.session-files.{sessionId}`
 * Schema version: 1 — bump if shape changes.
 */

import { useCallback, useEffect, useState } from 'react';
import type { AttachmentMeta } from '@/components/organisms/ChatComposer';

const STORAGE_PREFIX = 'wayne.v1.session-files';
const MAX_FILES_PER_SESSION = 20; // cap to avoid unbounded localStorage growth

interface StoredFile extends AttachmentMeta {
  readonly uploadedAt: number; // epoch ms
}

function storageKey(sessionId: string): string {
  return `${STORAGE_PREFIX}.${sessionId}`;
}

function readStored(sessionId: string): StoredFile[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(storageKey(sessionId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Basic shape validation
    return (parsed as unknown[]).filter(
      (item): item is StoredFile =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as Record<string, unknown>).name === 'string' &&
        typeof (item as Record<string, unknown>).key === 'string' &&
        typeof (item as Record<string, unknown>).size === 'number',
    );
  } catch {
    return [];
  }
}

function writeStored(sessionId: string, files: StoredFile[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(sessionId), JSON.stringify(files));
  } catch {
    // Quota exceeded or private browsing — silently ignore
  }
}

export interface UseSessionFilesResult {
  /** All files ever uploaded in this session, newest first. */
  readonly sessionFiles: readonly StoredFile[];
  /** Record a newly-uploaded file into the session registry. */
  readonly registerFile: (file: AttachmentMeta) => void;
  /** Remove a file from the session registry (does not delete from S3). */
  readonly removeFile: (key: string) => void;
  /** Clear all session files from storage. */
  readonly clearSessionFiles: () => void;
}

export function useSessionFiles(sessionId: string): UseSessionFilesResult {
  const [sessionFiles, setSessionFiles] = useState<StoredFile[]>(() =>
    readStored(sessionId),
  );

  // Re-read from storage when sessionId changes (user switches conversation)
  useEffect(() => {
    setSessionFiles(readStored(sessionId));
  }, [sessionId]);

  const registerFile = useCallback(
    (file: AttachmentMeta) => {
      setSessionFiles((prev) => {
        // Deduplicate by key
        const filtered = prev.filter((f) => f.key !== file.key);
        const next: StoredFile[] = [
          { ...file, uploadedAt: Date.now() },
          ...filtered,
        ].slice(0, MAX_FILES_PER_SESSION);
        writeStored(sessionId, next);
        return next;
      });
    },
    [sessionId],
  );

  const removeFile = useCallback(
    (key: string) => {
      setSessionFiles((prev) => {
        const next = prev.filter((f) => f.key !== key);
        writeStored(sessionId, next);
        return next;
      });
    },
    [sessionId],
  );

  const clearSessionFiles = useCallback(() => {
    writeStored(sessionId, []);
    setSessionFiles([]);
  }, [sessionId]);

  return { sessionFiles, registerFile, removeFile, clearSessionFiles };
}
